import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardBody, CardFooter } from '@winnin/winnin_ui.components.card'
import { Title } from '@winnin/winnin_ui.components.title'

import { ChipStatus } from 'components/ChipStatus'
import { DASHBOARD_USECASES } from 'consts/dashboardUseCases'
import { jobTypeOptions } from 'consts/jobTypeOptions'
import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { useDashboardOptions, useIsMobileDevice } from 'hooks'
import { useTranslate } from 'lib/tolgee'
import { PanelStore, UserStore } from 'stores'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'
import { getJobUpdatedDate } from 'utils/getJobUpdatedDate'
import { mapBriefingStatus } from 'utils/mapBriefingStatus'
import { mapJobType } from 'utils/mapJobType'

import { CardThumbnail } from './components/CardThumbnail'
import { DashboardOptionsDropdown } from './components/DashboardOptionsDropdown'
import { WorkspaceJobType } from './consts'

import './index.scss'

export const DashboardCard = ({ job }) => {
  const t = useTranslate()
  const {
    setDashboard,
    setIsEditDashboardModalOpen,
    setIsDeleteDashboardModalOpen,
    setIsSelectDashboardFolderModalOpen,
  } = useDashboardModals()
  const { duplicateDashboard, copyDashboardLink } = useDashboardOptions()
  const isMobileDevice = useIsMobileDevice()

  const workspaceId = PanelStore?.selectedPanel?.id
  const isUserViewer = UserStore?.getRole === 'viewer'

  const shouldHideDasboardOptions = isUserViewer || isMobileDevice

  const { id, name, updatedAt, type, description, thumbnails, isAutomaticallyGenerated, publicationStatus } = job

  const isBriefing = [WorkspaceJobType.AutoBriefingSocial, WorkspaceJobType.Briefing].includes(type)

  const jobType =
    DASHBOARD_USECASES.find(jobTypeOption => jobTypeOption.value === type) ??
    jobTypeOptions.find(jobTypeOption => jobTypeOption.value === type)

  const link = useMemo(() => {
    return `/workspace/${workspaceId}/jobs/${id}/dashboard`
  }, [id, workspaceId])

  const handleEditClick = job => {
    const { id, name, description, isAutomaticallyGenerated } = job
    setDashboard({
      id,
      name,
      description: isAutomaticallyGenerated ? null : description,
      jobType: mapJobType(type),
    })
    setIsEditDashboardModalOpen(true)
  }

  const handleRenameClick = job => {
    const { id, name, description, isAutomaticallyGenerated } = job

    setDashboard({
      id,
      name,
      description: isAutomaticallyGenerated ? null : description,
      jobType: mapJobType(type),
    })
    setIsEditDashboardModalOpen(true)
  }

  const handleDeleteClick = job => {
    const { id, name } = job

    setDashboard({ id, name, jobType: mapJobType(type) })
    setIsDeleteDashboardModalOpen(true)
  }

  const handleOpenFolderModal = () => {
    const { DASHBOARD_FOLDERS } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(DASHBOARD_FOLDERS.OPEN_ADD_TO_FOLDER, {
      id,
      name,
      description: isAutomaticallyGenerated ? null : description,
      jobType: mapJobType(type),
    })
    setDashboard({ id, name, jobType: mapJobType(type) })
    setIsSelectDashboardFolderModalOpen(true)
  }

  const dashboardOptions = [
    {
      label: t(TOLGEE_GLOBALS.moveToFolder),
      onClick: handleOpenFolderModal,
    },
    {
      label: isAutomaticallyGenerated ? t(TOLGEE_GLOBALS.rename) : t(TOLGEE_GLOBALS.edit),
      onClick: isAutomaticallyGenerated ? () => handleRenameClick(job) : () => handleEditClick(job),
    },
    {
      label: t(TOLGEE_GLOBALS.delete),
      onClick: () => handleDeleteClick(job),
    },
    {
      label: t(TOLGEE_GLOBALS.duplicate),
      onClick: () => duplicateDashboard(job),
    },
    {
      label: t(TOLGEE_GLOBALS.copyLink),
      onClick: () => copyDashboardLink(job),
    },
  ]

  return (
    <Link to={link}>
      <div className="DashboardCardCompose-container">
        {!shouldHideDasboardOptions && <DashboardOptionsDropdown options={dashboardOptions} />}

        <Card className="card-wrapper">
          <CardThumbnail thumbnails={thumbnails} isBriefing={isBriefing} />

          <CardBody
            paddingTop="var(--spacing_unit_4)"
            paddingBottom="var(--spacing_unit_4)"
            className="DashboardCard-body"
          >
            <div className="DashboardCard-body-badges">
              <ChipStatus size="small" status={mapBriefingStatus(publicationStatus)} />
              {isAutomaticallyGenerated && <ChipStatus size="small" status="zai" />}
            </div>

            <div className="DashboardCard-body-content">
              <Title tag="h5" className="DashboardCard-title" title={name}>
                {name}
              </Title>
              {jobType && <p className="DashboardCard-jobType">{t(jobType.label)}</p>}
              {description && (
                <p className="DashboardCard-description" title={description}>
                  {description}
                </p>
              )}
            </div>
          </CardBody>

          {updatedAt && (
            <CardFooter paddingTop={0}>
              <p className="DashboardCard-details" title={getJobUpdatedDate(job, t)}>
                {getJobUpdatedDate(job, t)}
              </p>
            </CardFooter>
          )}
        </Card>
      </div>
    </Link>
  )
}
